import React, { useState } from 'react'
import styles from './FormCarrozzerieModal.module.sass'
import {
  Button,
  CriterioForm,
  FormField,
  FormInput,
  FormInputPassword,
  FormLayout,
  FormikField,
  Modal,
} from '@next-nx/shared-ui/atoms'
import { useTealium, useTrans } from '@next-nx/hooks'
import Link from 'next/link'
import { Logo, SuccessCircle } from '@next-nx/shared-ui/icons'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import {
  useLoginMutation,
  useRecuperaPasswordMutation,
  useResetPasswordMutation,
} from '@next-nx/shared-graphql/unipolrental'
import { getTimestamp, isLandingCarrozzerie } from '@next-nx/utils'
import { TypDataTracking } from '@next-nx/utils/types'

interface Props {
  className?: string
  variant: 'login' | 'recupera_password' | 'ripristina_password' | 'success'
  onChangeVariant?: (v: 'login' | 'recupera_password' | 'ripristina_password' | 'success') => void
  token?: string
  setToken?: any
}

const FormCarrozzerieModal = (props: Props) => {
  const {
    className = '',
    variant = 'login',
    onChangeVariant = () => {},
    token = '',
    setToken = undefined,
  } = props

  const t = useTrans()

  const [login, { loading }] = useLoginMutation()
  const [recuperaPassword, { loading: loadingRecuperaPassword }] = useRecuperaPasswordMutation()
  const [resetPassword, { loading: loadingResetPassword }] = useResetPasswordMutation()
  const [error, setError] = useState<string | undefined | null>()
  const [message, setMessage] = useState<string | undefined | null>()

  const { linkDataLayer, getProdottoData } = useTealium()

  const initialValues = {
    email: '',
    password: '',
  }

  const handleTracking = (step: number, data?: TypDataTracking, type?: string) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}

    switch (step) {
      case 4:
        dlObj = {
          virtual: 'action',
          action_name: 'login',
          action_tech: 'click',
          action_type: type ? type : '',
          action_timestamp,
          action_effect: 'login_carrozzeria',
          market: 'b2b',
          id_carrozzeria: data?.id_carrozzeria ? data?.id_carrozzeria : '-',
        }
        break
      case 5:
        dlObj = {
          virtual: 'action',
          action_name: 'pw_dimenticata',
          action_tech: 'click',
          action_type: type ? type : '',
          action_timestamp,
          action_effect: `pw_dimenticata_carrozzeria`,
          market: 'b2b',
          id_carrozzeria: data?.id_carrozzeria ? data?.id_carrozzeria : '-',
        }
        break
      case 6:
        dlObj = {
          virtual: 'action',
          action_name: 'pw_ripristino',
          action_tech: 'click',
          action_type: 'imposta_pw',
          action_timestamp,
          action_effect: 'pw_rispristino_carrozzeria',
          market: 'b2b',
          id_carrozzeria: '-',
        }
        break
      default:
        dlObj
        break
    }
    linkDataLayer(dlObj)
  }

  const validationSchema = yup.object().shape({
    email:
      variant !== 'ripristina_password'
        ? yup
            .string()
            .email("Il formato dell'email dev'essere valido")
            .required('Campo obbligatorio')
            .nullable()
        : yup.string().nullable(),
    password:
      variant === 'login'
        ? yup.string().required('Campo obbligatorio')
        : variant === 'ripristina_password'
        ? yup
            .string()
            .required('Campo obbligatorio')
            .min(8, 'Password must be 8 characters long')
            .matches(/[A-Z]/, 'Password requires an uppercase letter')
            .matches(/[a-z]/, 'Password requires a lowercase letter')
            .matches(/[^\w]/, 'Password requires a symbol')
            .matches(/[0-9]/, 'Password requires a number')
        : yup.string().nullable(),
  })

  const showError = (formikProps, name) => {
    return !!formikProps.errors[name]
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        if (variant === 'login') {
          login({
            variables: {
              email: values.email,
              password: values.password,
            },
          })
            .then((res) => {
              setToken(res.data?.tokenAuth?.token)
              handleTracking(
                4,
                { id_carrozzeria: res.data?.tokenAuth?.user?.hashEmail },
                'login_ok'
              )
              window.location.href = window.location.href.split('?')?.[0]
              setError('')
              setMessage('')
            })
            .catch((e) => {
              setMessage('')
              setError('Le credenziali inserite non sono corrette.')
            })
        } else if (variant === 'recupera_password') {
          recuperaPassword({
            variables: {
              email: values.email,
            },
          })
            .then((res) => {
              if (res?.data?.recuperaPassword?.status) {
                setError('')
                setMessage(
                  t(
                    'Controlla la casella di posta, ti abbiamo inviato una password temporanea da utilizzare al prossimo accesso.'
                  )
                )
                handleTracking(
                  5,
                  { id_carrozzeria: res?.data?.recuperaPassword?.hashEmail },
                  'invia_email'
                )
                actions.resetForm()
              } else {
                setMessage('')
                setError("L'email inserita non è corretta.")
              }
            })
            .catch((error) => {
              setMessage('')
              setError('Si è verificato un errore, ti preghiamo di riprovare più tardi')
            })
        } else if (variant === 'ripristina_password') {
          resetPassword({
            variables: {
              token,
              password: values?.password,
            },
          })
            .then((res) => {
              if (res?.data?.resetPassword?.status) {
                handleTracking(6, {})
                onChangeVariant('success')
                actions.resetForm()
              } else {
                setMessage('')
                setError(t('Il link per il cambio password non è più valido.'))
              }
            })
            .catch((err) => {
              setMessage('')
              setError(t('Il link per il cambio password non è più valido.'))
            })
        }

        actions.setSubmitting(false)
      }}
    >
      {(props) => (
        <Form>
          <Modal open={true} className={`${styles.body} ${styles[`body--${variant}`]}`}>
            {variant !== 'success' && (
              <div className={styles.logo}>
                <Link href="/">
                  <a className={styles.logo} target="_blank">
                    <Logo />
                  </a>
                </Link>
              </div>
            )}
            {variant !== 'success' && (
              <div>
                <div className={styles.titolo}>
                  {variant === 'login'
                    ? t('Effettua il login oppure registrati per visualizzare il contenuto')
                    : variant === 'recupera_password'
                    ? t('Password dimenticata?')
                    : t('Imposta una nuova password')}
                </div>
                <div className={styles.descrizione}>
                  {variant === 'recupera_password'
                    ? t(
                        'Inserisci l’indirizzo e-mail con il quale sei registrato. Ti invieremo le istruzioni per ripristinare la password.'
                      )
                    : variant === 'ripristina_password'
                    ? t('Inserisci una nuova password da utilizzare per accedere.')
                    : ''}
                </div>
              </div>
            )}
            {variant === 'login' && (
              <div className={styles.formLogin}>
                <FormLayout>
                  <FormikField
                    className={styles.field}
                    formik={props}
                    label="E-mail"
                    name="email"
                    type="email"
                    placeholder="Inserisci l’e-mail di accesso"
                    required
                  />
                  <FormikField
                    className={styles.field}
                    formik={props}
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Inserisci la password"
                    required
                  />

                  {!!error && (
                    <div className={`${styles.message} ${styles['message--error']}`}>{error}</div>
                  )}

                  <div className={styles.passwordDimenticata}>
                    <Button
                      label={t('Hai dimenticato la password ?')}
                      onClick={() => {
                        onChangeVariant('recupera_password')
                        handleTracking(4, {}, 'hai_dimenticato_la_password')
                        setError('')
                        setMessage('')
                      }}
                      variant="ghost"
                    />
                  </div>
                  <Button
                    label={'Accedi'}
                    className={`${styles.submitButton}  ${styles['submitButton--login']}`}
                    onClick={() => {
                      props.handleSubmit()
                    }}
                    disabled={!Object.keys(props.touched).length || !props.isValid}
                    loading={props.isSubmitting}
                  />
                </FormLayout>
              </div>
            )}

            {variant === 'recupera_password' && (
              <div className={styles.formRecuperaPassword}>
                <FormLayout>
                  <FormikField
                    className={styles.field}
                    formik={props}
                    label="E-mail"
                    name="email"
                    type="email"
                    placeholder="Inserisci l’e-mail di accesso"
                    required
                    showError={showError}
                  />
                  {!!error && (
                    <div className={`${styles.message} ${styles['message--error']}`}>{error}</div>
                  )}
                  {!!message && <div className={styles.message}>{message}</div>}

                  <div className={styles.recuperaPasswordButtons}>
                    <Button
                      label={'Invia e-mail'}
                      className={`${styles.submitButton}`}
                      onClick={() => props.handleSubmit()}
                      disabled={!Object.keys(props.touched).length || !props.isValid}
                      loading={props.isSubmitting}
                    />
                    <div className={styles.tornaAlLogin}>
                      <Button
                        label={t('Torna al login')}
                        onClick={() => {
                          onChangeVariant('login')
                          handleTracking(5, {}, 'torna_al_login')
                          setError('')
                        }}
                        variant="ghost"
                      />
                    </div>
                  </div>
                </FormLayout>
              </div>
            )}

            {variant === 'ripristina_password' && (
              <div className={styles.formRipristinaPassword}>
                <FormLayout>
                  <FormikField
                    className={styles.field}
                    formik={props}
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Inserisci la password"
                    required
                    showErrorMessage={false}
                  />

                  <div className={styles.criteriSection}>
                    <div className={styles.criteriTitle}>{t('Criteri della nuova password:')}</div>
                    <div className={styles.criteriList}>
                      <div className={styles.criterio}>
                        <CriterioForm
                          label={'Almeno 8 caratteri'}
                          checked={props.values.password.length >= 8}
                        />
                      </div>
                      <div className={styles.criterio}>
                        <CriterioForm
                          label={'1 maiuscola'}
                          checked={/[A-Z]/.test(props.values.password)}
                        />
                      </div>
                      <div className={styles.criterio}>
                        <CriterioForm
                          label={'1 minuscola'}
                          checked={/[a-z]/.test(props.values.password)}
                        />
                      </div>
                      <div className={styles.criterio}>
                        <CriterioForm
                          label={'1 carattere speciale'}
                          checked={/[^\w]/.test(props.values.password)}
                        />
                      </div>
                      <div className={styles.criterio}>
                        <CriterioForm
                          label={'1 numero'}
                          checked={/[0-9]/.test(props.values.password)}
                        />
                      </div>
                    </div>
                  </div>

                  {!!error && (
                    <div className={`${styles.message} ${styles['message--error']}`}>{error}</div>
                  )}

                  <Button
                    label={'Imposta la password'}
                    className={`${styles.submitButton} ${styles['submitButton--login']}`}
                    onClick={() => props.handleSubmit()}
                    disabled={!Object.keys(props.touched).length || !props.isValid}
                    loading={props.isSubmitting}
                  />
                </FormLayout>
              </div>
            )}

            {variant === 'success' && (
              <div className={styles.success}>
                <div className={styles.successIcon}>
                  <div className={styles.icon}>
                    <SuccessCircle />
                  </div>
                </div>
                <div className={`${styles.titolo} ${styles['titolo--success']}`}>
                  {t('Password impostata correttamente')}
                </div>
                <div className={`${styles.descrizione} ${styles['descrizione--success']}`}>
                  {t('success_login_carrozzerie_descrizione')}
                </div>
                <div className={styles.successButton}>
                  <Button
                    label={t('Vai al login')}
                    className={`${styles.submitButton}`}
                    onClick={() => onChangeVariant('login')}
                  />
                </div>
              </div>
            )}
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default FormCarrozzerieModal
